// Pure helpers behind the Position Volatility (7D) table.

/**
 * Normalize a position-volatility payload into the row array the table renders.
 *
 * Two shapes have shipped for this endpoint:
 *   - Express/MySQL query — `{ status, runs_available, rows: [{ query, provider, …, positions }] }`
 *   - Supabase RPC        — `{ status, runs_available, client: [{ prompt_text, provider, … }], competitors: [...] }`
 *
 * The Supabase RPC split `rows` into `client` / `competitors` and renamed `query` to
 * `prompt_text`, so a consumer reading `info.rows` sees nothing at all. This
 * maps either shape onto `{ query, provider, avg_position, best, worst,
 * volatility, positions }`.
 *
 * `positions` (the per-run series behind the Trend sparkline) is absent from
 * the Supabase RPC. Backend has not said why, and measuring volatility across
 * runs is not itself a reason the series could not exist — the Express query it
 * replaced carried one. It comes back `undefined`, which `Sparkline` already
 * renders as empty. See `hasPositionSeries`.
 *
 * @param {object|null|undefined} info the endpoint payload
 * @returns {Array<object>} rows for the client's own queries
 */
export function positionVolatilityRows(info) {
  const source = Array.isArray(info?.rows)
    ? info.rows
    : (Array.isArray(info?.client) ? info.client : []);
  return source
    .filter((r) => r && typeof r === 'object')
    .map((r) => ({
      ...r,
      query: r.query ?? r.prompt_text ?? '',
    }));
}

/**
 * Whether any row carries enough per-run history to draw a Trend sparkline.
 *
 * `Sparkline` needs at least two points. `fn_get_aim_position_volatility`
 * supplies none — its `runs` field is a count, not the positions array — and no
 * other RPC can fill the gap: organic-rankings has no provider dimension, while
 * this table spans ten providers. So under that shape the column renders nothing
 * on every row, and an empty column reads as a broken one. The card hides it and
 * says why instead. The Express query it replaced did carry the series, so this
 * is a per-payload question rather than a permanent one.
 *
 * @param {Array<object>|null|undefined} rows normalized volatility rows
 * @returns {boolean} true when at least one row has a drawable series
 */
export function hasPositionSeries(rows) {
  return (Array.isArray(rows) ? rows : [])
    .some((r) => Array.isArray(r?.positions) && r.positions.length >= 2);
}

/**
 * Human-readable label for the payload's `scope` field.
 *
 * `fn_get_aim_position_volatility` accepts `p_run_id` and ignores it: volatility
 * is measured across every completed run, so the figures do not belong to the
 * run selected above the panel. The RPC returns `scope` so the panel can say so
 * rather than letting the run picker imply otherwise.
 *
 * @param {object|null|undefined} info the endpoint payload
 * @returns {string|null} the label, or null when the payload states no scope
 */
export function positionVolatilityScopeLabel(info) {
  const scope = info?.scope;
  if (typeof scope !== 'string' || !scope) return null;
  if (scope === 'across_all_completed_runs') {
    const runs = Number(info?.runs_available);
    return Number.isFinite(runs) && runs > 0
      ? `Measured across all ${runs} completed runs, not the run selected above.`
      : 'Measured across all completed runs, not the run selected above.';
  }
  return `Scope: ${scope.replace(/_/g, ' ')}.`;
}
